import { useEffect, useState } from "react";
import { api, formatDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { RefreshCw, ShieldCheck, User, Sparkles } from "lucide-react";

export default function Settings() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    api.get("/audit-logs?limit=50").then((r) => setLogs(r.data));
  }, []);

  const refreshLogs = async () => {
    const { data } = await api.get("/audit-logs?limit=50");
    setLogs(data);
  };

  const reseed = async () => {
    if (!confirm("This will replace all your existing entries with demo data. Continue?")) return;
    await api.post("/demo/seed");
    toast.success("Demo data reseeded");
    refreshLogs();
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <div className="label-overline">Account</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mt-1">
          Settings
        </h1>
      </div>

      <Card className="p-6">
        <div className="flex items-start gap-4">
          <div className="h-10 w-10 rounded-full bg-slate-900 text-white flex items-center justify-center">
            <User className="h-5 w-5" strokeWidth={1.75} />
          </div>
          <div className="flex-1">
            <div className="font-heading font-semibold text-lg">{user?.full_name}</div>
            <div className="text-sm text-slate-600">{user?.email}</div>
            <div className="mt-2 flex gap-2">
              <Badge variant="outline" className="capitalize"><ShieldCheck className="h-3 w-3 mr-1" />{user?.role}</Badge>
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-heading font-semibold">Demo Data</div>
            <div className="text-sm text-slate-600 mt-1">
              Replace all entries with a fresh set of realistic India-first demo data.
            </div>
          </div>
          <Button variant="outline" onClick={reseed} data-testid="settings-reseed-button">
            <Sparkles className="h-4 w-4 mr-2" /> Reseed demo
          </Button>
        </div>
      </Card>

      <Card className="overflow-hidden" data-testid="settings-audit-card">
        <div className="p-4 border-b bg-slate-50 flex items-center justify-between">
          <div className="font-heading font-semibold">Audit Log</div>
          <Button variant="ghost" size="sm" onClick={refreshLogs}>
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>
        <div className="max-h-[500px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50/70 text-left border-b">
                <th className="px-4 py-2 font-medium">Time</th>
                <th className="px-4 py-2 font-medium">Action</th>
                <th className="px-4 py-2 font-medium">Module</th>
                <th className="px-4 py-2 font-medium">Details</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id} className="border-b hover:bg-slate-50/50">
                  <td className="px-4 py-2 tabular-nums text-slate-500">{formatDate(l.created_at)}</td>
                  <td className="px-4 py-2">
                    <Badge variant="outline" className="text-[10px]">{l.action}</Badge>
                  </td>
                  <td className="px-4 py-2 text-slate-600">{l.module || "—"}</td>
                  <td className="px-4 py-2 text-slate-600 truncate max-w-[280px]">{l.details || "—"}</td>
                </tr>
              ))}
              {logs.length === 0 && (
                <tr><td colSpan={4} className="p-6 text-center text-slate-500">No activity yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
